import React from 'react';
import { Link } from 'react-router-dom';
import WhiskeyLogo from '../layout/WhiskeyLogo';
import { LEGAL, LEGAL_HAS_PLACEHOLDERS } from '../../config/legal';

interface LegalPageProps {
  title: string;
  summary: string;
  children: React.ReactNode;
}

export const Section: React.FC<{ heading: string; children: React.ReactNode }> = ({ heading, children }) => (
  <section style={{ marginBottom: 30 }}>
    <h2 className="ws-display" style={{ margin: '0 0 10px', fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>
      {heading}
    </h2>
    <div style={{ fontSize: 14.5, lineHeight: 1.65, color: 'var(--text-secondary)' }}>{children}</div>
  </section>
);

export const List: React.FC<{ items: React.ReactNode[] }> = ({ items }) => (
  <ul style={{ margin: '10px 0', paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 7 }}>
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

/** Definition-style row used for the data and subprocessor tables. */
export const Row: React.FC<{ term: string; children: React.ReactNode }> = ({ term, children }) => (
  <div style={{ display: 'flex', gap: 16, padding: '10px 0', borderTop: '1px solid var(--border)', flexWrap: 'wrap' }}>
    <span className="ws-display" style={{ flex: '0 0 190px', fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)' }}>
      {term}
    </span>
    <span style={{ flex: '1 1 260px', minWidth: 0, fontSize: 13.5, lineHeight: 1.6, color: 'var(--text-secondary)' }}>{children}</span>
  </div>
);

const LEGAL_LINKS = [
  { to: '/privacy', label: 'Privacy Policy' },
  { to: '/terms', label: 'Terms of Use' },
  { to: '/data-rights', label: 'Your data rights' },
];

/**
 * Shared shell for the legal documents. Reachable signed in or signed out, and
 * deliberately outside the app chrome — someone reading a privacy policy should
 * not be looking at a Dashboard sidebar.
 */
const LegalPage: React.FC<LegalPageProps> = ({ title, summary, children }) => (
  <div className="ws-shell" style={{ minHeight: '100vh', background: 'var(--paper)' }}>
    <div style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '18px 28px' }}>
        <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 9, textDecoration: 'none' }}>
          <WhiskeyLogo size={20} />
          <span className="ws-display" style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' }}>
            {LEGAL.SITE_NAME}
          </span>
        </Link>
      </div>
    </div>

    <div style={{ maxWidth: 760, margin: '0 auto', padding: '40px 28px 64px' }}>
      {LEGAL.STATUS === 'draft' && (
        <div
          role="note"
          style={{
            marginBottom: 28,
            padding: '14px 16px',
            borderRadius: 10,
            border: '1.5px solid var(--warn)',
            background: 'oklch(0.72 0.16 75 / 0.09)',
            fontSize: 13.5,
            lineHeight: 1.6,
            color: 'var(--text-primary)',
          }}
        >
          <strong>Draft — not yet in force.</strong> This document describes how {LEGAL.SITE_NAME} actually
          works today, but it has not been reviewed by a lawyer
          {LEGAL_HAS_PLACEHOLDERS ? ' and still contains unfilled details' : ''}. Do not rely on it as a
          legal agreement yet.
        </div>
      )}

      <h1 className="ws-display" style={{ margin: '0 0 8px', fontSize: 32, fontWeight: 700, letterSpacing: '-0.015em', color: 'var(--text-primary)' }}>
        {title}
      </h1>
      <p style={{ margin: '0 0 6px', fontSize: 15, lineHeight: 1.6, color: 'var(--text-secondary)' }}>{summary}</p>
      <p className="ws-mono" style={{ margin: '0 0 34px', fontSize: 12, color: 'var(--text-tertiary)' }}>
        Last updated {LEGAL.LAST_UPDATED}
      </p>

      {children}

      <div style={{ marginTop: 40, paddingTop: 20, borderTop: '1px solid var(--border)', display: 'flex', gap: 18, flexWrap: 'wrap' }}>
        {LEGAL_LINKS.filter((l) => l.label !== title).map((l) => (
          <Link key={l.to} to={l.to} style={{ fontSize: 13, fontWeight: 700, color: 'var(--ember-strong)', textDecoration: 'none' }}>
            {l.label}
          </Link>
        ))}
        <Link to="/" style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-tertiary)', textDecoration: 'none' }}>
          Back to {LEGAL.SITE_NAME}
        </Link>
      </div>
    </div>
  </div>
);

export default LegalPage;
